import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, Flame, Mountain, CalendarHeart, ArrowRight, Phone } from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { getPoojas } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { waLink, generalWaMessage } from "@/lib/whatsapp";

const TempleHistory = () => {
  const site = useSite();
  const { data: poojas = [] } = useQuery({ queryKey: ["poojas", "all"], queryFn: () => getPoojas() });
  const img1 = "https://images.pexels.com/photos/31969428/pexels-photo-31969428.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1100";
  const img2 = "https://images.pexels.com/photos/38122489/pexels-photo-38122489.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1100";

  return (
    <Layout>
      <Seo
        title="Gokarna Mahabaleshwar Temple History & Significance | Atmalinga"
        description="Discover the history and spiritual significance of the Gokarna Mahabaleshwar Temple, the sacred Atmalinga, festivals, and why poojas performed here are especially powerful."
      />
      <section className="relative h-[45vh] min-h-[340px]">
        <img src={img2} alt="Gokarna temple" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2C1E16]/95 via-[#2C1E16]/55 to-[#2C1E16]/30" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 h-full flex flex-col justify-end pb-10">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-gold">Dakshina Kashi</span>
          <h1 className="mt-2 font-heading text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight max-w-3xl">The Sacred Gokarna Mahabaleshwar Temple</h1>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 sm:px-8 lg:px-12 py-16 prose-temple">
        <h2>The Legend of the Atmalinga</h2>
        <p>Gokarna is one of the most revered Shiva kshetras in India, home to the sacred <strong>Atmalinga</strong>. According to tradition, the demon king Ravana obtained the Atmalinga from Lord Shiva through intense penance. On his way to Lanka he was tricked into setting it down at Gokarna, where it became immovably fixed in the earth. For this reason Gokarna is considered as spiritually potent as Kashi (Varanasi) — earning it the name <strong>Dakshina Kashi</strong> ("Kashi of the South").</p>

        <h2>Spiritual Significance</h2>
        <p>The Atmalinga is believed to grant the same merit as worshipping the soul of Shiva himself. Devotees travel from across India to seek darshan and to perform rituals here, especially those connected to liberation, ancestral peace, and the removal of deep-rooted doshas.</p>

        <h2>Why Poojas Here Are Special</h2>
        <p>Rituals performed at this powerful kshetra are believed to carry exceptional spiritual potency. Gokarna is particularly renowned for <strong>Rudrabhishek</strong>, <strong>Sarpa Samskara (Rahu-Ketu remedies)</strong>, and <strong>ancestral rites (Pitru karya)</strong>. Performed by hereditary priests using complete Vedic procedure, these poojas connect devotees directly to centuries of unbroken tradition.</p>

        <h2>Festivals & Pilgrimage</h2>
        <p><strong>Maha Shivaratri</strong> is the grandest festival here, drawing thousands of devotees. The temple town also blends spirituality with serene beaches, making Gokarna both a sacred pilgrimage and a peaceful retreat for families.</p>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 pb-8 grid sm:grid-cols-2 gap-4">
        <img src={img1} alt="Ancient temple" loading="lazy" className="rounded-xl object-cover w-full aspect-[16/10]" />
        <div className="grid grid-cols-2 gap-4">
          {[
            { Icon: Flame, label: "Atmalinga Darshan" },
            { Icon: Mountain, label: "Sacred Kshetra" },
            { Icon: Sparkles, label: "Potent Rituals" },
            { Icon: CalendarHeart, label: "Maha Shivaratri" },
          ].map(({ Icon, label }) => (
            <div key={label} className="bg-cream border border-border rounded-xl p-6 flex flex-col justify-center">
              <Icon className="h-7 w-7 text-primary" />
              <p className="mt-3 font-heading text-xl text-secondary leading-tight">
                {label}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-secondary text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-3xl text-white">Plan your pooja at Gokarna</h2>
            <p className="mt-2 text-white/75">We help schedule your ritual at the most auspicious time around your visit.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/poojas" className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-semibold text-white hover:bg-[#CC5204]" data-testid="temple-view-poojas">View Poojas <ArrowRight className="h-4 w-4" /></Link>
            <a href={waLink(site.whatsapp_number, generalWaMessage)} target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-2 rounded-md bg-whatsapp px-6 py-3 font-semibold text-white hover:opacity-90" data-testid="temple-whatsapp"><Phone className="h-4 w-4" /> WhatsApp</a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TempleHistory;
